import React, { useState, useEffect } from 'react';
import { dataService } from '../../../../Services/data/data.service';
import { makeStyles } from "@material-ui/core/styles";
import { toast } from 'react-toastify';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import { X } from 'lucide-react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Chip from '../../../Shared/ui/Chip';
import { costBundleTypes } from '../../../../Config/constants';
import Button from '../../../Shared/Buttons/Button';

export default function BulkUpdatePriceBundleDialog({ 
    dialogOpen, 
    closeDialog, 
    projectDetailRows, 
    allPriceBundles, 
    currency, 
    userId, 
    onSuccess,
    ...props 
}) {
    const classes = useStyles();

    const [generalError, setGeneralError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [effectiveDate, setEffectiveDate] = useState(new Date());
    const [detailsWithPriceBundles, setDetailsWithPriceBundles] = useState([]);

    useEffect(() => {
        if (dialogOpen && projectDetailRows && allPriceBundles) {
            // Filter project details that have price bundles set
            const detailsWithBundles = projectDetailRows
                .filter(row => row.priceBundleId != null)
                .map(row => {
                    // Match price bundle by ID (use 'id' field as seen in TableDragResources)
                    const priceBundle = allPriceBundles.find(pb => pb.id === row.priceBundleId);
                    
                    let priceBundleName = 'Unknown Price Bundle';
                    if (priceBundle) {
                        // Format like in TableDragResources: reference - name
                        priceBundleName = `${priceBundle.reference} - ${priceBundle.name}`;
                    } else {
                        // Debug fallback - show the ID if no match found
                        priceBundleName = `Price Bundle ID: ${row.priceBundleId}`;
                    }
                    
                    return {
                        id: row.Id,
                        description: row.description,
                        priceBundleId: row.priceBundleId,
                        priceBundleName: priceBundleName
                    };
                });
            
            setDetailsWithPriceBundles(detailsWithBundles);
        }
    }, [dialogOpen, projectDetailRows, allPriceBundles]);

    const handleCloseDialog = () => {
        setGeneralError(null);
        setIsLoading(false);
        setEffectiveDate(new Date());
        setDetailsWithPriceBundles([]);
        closeDialog();
    };

    // Helper function to calculate total bundle price (from TableDragResources)
    const getTotalBundlePrice = (priceBundleItems) => {
        if (priceBundleItems && priceBundleItems.length > 0) {
            return (
                currency.symbol +
                priceBundleItems
                    .reduce((total, item) => {
                        const price = typeof item.price === "string" ? parseFloat(item.price.replace(",", ".")) : parseFloat(item.price || 0);
                        return total + price * (item.multiplier || 1);
                    }, 0)
                    .toFixed(3)
            );
        }
        return "0.00 " + currency.symbol;
    };

    const formatPriceBundle = (detail) => {
        if (!allPriceBundles || allPriceBundles.length === 0) {
            return <span>Unknown Price Bundle</span>;
        }
        
        const priceBundle = allPriceBundles.find(pb => pb.id === detail.priceBundleId);
        
        if (!priceBundle) {
            return <span>Price Bundle ID: {detail.priceBundleId}</span>;
        }
        
        // Determine pricing type suffix based on costBundleTypeId
        const pricingTypeSuffix = priceBundle.costBundleTypeId === costBundleTypes.TIME ? "/hour" : "/event";
        
        return (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span>{priceBundle.reference} - {priceBundle.name}</span>
                <Chip 
                    size="extraSmall" 
                    variant={priceBundle.account && priceBundle.account.isRevenue ? "success" : "danger"}
                >
                    {getTotalBundlePrice(priceBundle.priceBundleItems)}
                    {pricingTypeSuffix}
                </Chip>
            </div>
        );
    };

    const handleBulkUpdate = async () => {
        if (!effectiveDate) {
            setGeneralError('Please select an effective date.');
            return;
        }

        if (detailsWithPriceBundles.length === 0) {
            setGeneralError('No project details with price bundles found.');
            return;
        }

        setIsLoading(true);
        setGeneralError(null);

        try {
            // Prepare bulk update data for single API call
            const bulkUpdateData = {
                updates: detailsWithPriceBundles.map(detail => ({
                    projectDetailId: detail.id,
                    priceBundleId: detail.priceBundleId
                })),
                applyFromDate: effectiveDate,
                userId: userId
            };

            // Execute single bulk update with database transaction
            const result = await dataService.bulkUpdateProjectDetailCostBundle(bulkUpdateData);
            
            if (result && result.success) {
                // All updates successful
                toast.success(`Successfully updated price bundles for ${detailsWithPriceBundles.length} project details from ${effectiveDate.toLocaleDateString()}`, {
                    position: toast.POSITION.TOP_CENTER,
                    autoClose: 3000,
                });

                if (onSuccess) {
                    onSuccess();
                }

                handleCloseDialog();
            } else {
                setGeneralError('Failed to update price bundles. Please try again.');
                setIsLoading(false);
            }

        } catch (error) {
            console.error('Error updating price bundles:', error);
            setGeneralError('An error occurred while updating price bundles. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Dialog 
            className={classes.dialog}
            open={dialogOpen}
            aria-labelledby="bulk-update-dialog-title"
            onClose={handleCloseDialog}
            classes={{ paper: classes.dialogPaper }}
            maxWidth="sm"
            fullWidth
        >
            <div className={classes.dialog__head}>
                <DialogTitle className={classes.dialog__title} id="bulk-update-dialog-title">
                    Bulk Update Price Bundles
                </DialogTitle>
                <button onClick={handleCloseDialog} className={classes.dialog__closeButton}>
                    <X size={19} />
                </button>
            </div>
            
            <DialogContent className={classes.dialog__contentContainer}>
                <div className={classes.dialog__content}>
                    {generalError && (
                        <div className={classes.errorContainer}>
                            <p className={classes.errorMessage}>{generalError}</p>
                        </div>
                    )}
                    
                    <p className={classes.dialog__subtitle}>
                        This will update all measurements for the selected project details to use their current price bundles from the specified effective date.
                    </p>

                    <div className={classes.datePickerContainer}>
                        <label className={classes.dateLabel}>Effective Date</label>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                                value={effectiveDate}
                                onChange={(date) => setEffectiveDate(date)}
                                format="dd/MM/yyyy"
                                slotProps={{
                                    textField: {
                                        className: classes.datePicker,
                                        placeholder: "Select effective date"
                                    }
                                }}
                            />
                        </LocalizationProvider>
                    </div>

                    {detailsWithPriceBundles.length > 0 ? (
                        <div className={classes.detailsContainer}>
                            <h4 className={classes.sectionTitle}>
                                Project Details with Price Bundles ({detailsWithPriceBundles.length})
                            </h4>
                            <div className={classes.detailsList}>
                                {detailsWithPriceBundles.map((detail) => (
                                    <div key={detail.id} className={classes.detailItem}>
                                        <div className={classes.detailInfo}>
                                            <span className={classes.detailDescription}>
                                                {detail.description}
                                            </span>
                                            <div className={classes.detailPriceBundle}>
                                                {formatPriceBundle(detail)}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className={classes.noDetailsContainer}>
                            <p className={classes.noDetailsMessage}>
                                No project details with price bundles found. Please set price bundles on your project details first.
                            </p>
                        </div>
                    )}
                </div>
            </DialogContent>
            
            <div className={classes.dialog__actions}>
                <Button 
                    variant='outlined' 
                    onClick={handleCloseDialog}
                    disabled={isLoading}
                >
                    Cancel
                </Button>
                <Button 
                    onClick={handleBulkUpdate}
                    disabled={isLoading || detailsWithPriceBundles.length === 0}
                >
                    {isLoading ? 'Updating...' : `Update ${detailsWithPriceBundles.length} Price Bundles`}
                </Button>
            </div>
        </Dialog>
    );
}

const useStyles = makeStyles(() => ({
    dialog: {
        margin: "auto"
    },

    dialogPaper: {
        width: '600px !important',
        maxWidth: '90vw !important',
        height: 'auto',
        borderRadius: '6px !important',
    },

    dialog__head: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        padding: '16px 16px 0px 16px !important',
        marginBottom: '8px !important',
    },

    dialog__title: {
        fontSize: '24px !important',
        fontFamily: "Lato, sans-serif !important",
        fontWeight: 'normal',
        padding: '0px !important',
    },

    dialog__closeButton: {
        backgroundColor: '#F5F5F5',
        border: 'none',
        cursor: 'pointer',
        padding: '3px !important',
        height: '25px !important',
        borderRadius: '3px',
        transition: 'all 0.2s',

        '&:hover': {
            backgroundColor: '#D84949',
            color: '#F5F5F5',
        },

        '&:focus-visible': {
            outline: '#4654FB auto 1px',
        },
    },

    dialog__subtitle: {
        fontSize: '16px',
        fontFamily: "Lato, sans-serif",
        fontWeight: 'normal',
        color: '#0C0E29',
        margin: '0px 0px 24px 0px',
        lineHeight: '1.4',
    },

    datePickerContainer: {
        marginBottom: '24px',
    },

    dateLabel: {
        fontSize: '16px',
        fontFamily: "Lato, sans-serif",
        color: '#0C0E29',
        display: 'block',
        marginBottom: '8px',
        fontWeight: '500',
    },

    datePicker: {
        width: '100%',
        
        '& .MuiInputBase-root': {
            fontSize: '14px',
        },
        
        '& .MuiOutlinedInput-root': {
            '&.Mui-focused fieldset': {
                borderColor: 'var(--color-primary)',
            },
        },
    },

    detailsContainer: {
        width: "100%",
        marginBottom: '0px',
    },

    sectionTitle: {
        fontSize: '16px',
        fontFamily: "Lato, sans-serif",
        fontWeight: '600',
        color: '#0C0E29',
        margin: '0px 0px 16px 0px',
    },

    detailsList: {
        width: "100%",
        maxHeight: '300px',
        overflowY: 'auto',
        border: '1px solid #EDEDED',
        borderRadius: '4px',
        padding: '8px',

        // Minimal scrollbar without arrows
        '&::-webkit-scrollbar': {
            width: '6px',
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#DCDCDC',
            borderRadius: '3px',
        },
        '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
        },
        '&::-webkit-scrollbar-button': {
            display: 'none', // Remove arrows
        },
        '-ms-overflow-style': 'none',  // IE and Edge
        scrollbarWidth: 'thin',        // Firefox
        scrollbarColor: '#EDEDED #F5F5F5', // Firefox
    },

    detailItem: {
        width: '100%',
        padding: '12px',
        borderBottom: '1px solid #F8F8F8',
        
        '&:last-child': {
            borderBottom: 'none',
        },
    },

    detailInfo: {
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '16px',
    },

    detailDescription: {
        fontSize: '16px',
        fontFamily: "Lato, sans-serif",
        fontWeight: '500',
        color: '#0C0E29',
        flex: '1',
        textAlign: 'left',
    },

    detailPriceBundle: {
        fontSize: '14px',
        fontFamily: "Lato, sans-serif",
        color: '#0C0E29',
        fontWeight: '400',
        textAlign: 'right',
        minWidth: '200px',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
    },

    noDetailsContainer: {
        padding: '24px',
        textAlign: 'center',
        backgroundColor: '#F8F8F8',
        borderRadius: '4px',
        marginTop: '16px',
    },

    noDetailsMessage: {
        fontSize: '16px',
        fontFamily: "Lato, sans-serif",
        color: '#666',
        margin: '0px',
    },

    dialog__contentContainer: {
        padding: '0px 16px !important',
        marginBottom: "24px",

        '&::-webkit-scrollbar': {
            display: 'none',
        },
        '-ms-overflow-style': 'none',
        scrollbarWidth: 'none',
    },

    dialog__content: {
        display: 'flex',
        height: '100%',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        margin: '0px !important',
    },

    dialog__actions: {
        display: 'flex',
        justifyContent: 'space-between',
        gap: '8px',
        padding: '0px 16px 16px 16px !important',
    },

    errorContainer: {
        width: "100%",
        backgroundColor: "#FDF3F3",
        border: "1px solid #FBE5E5",
        borderRadius: "6px",
        marginBottom: "16px",
        padding: "12px",
    },

    errorMessage: {
        fontSize: "16px",
        color: "#D84949",
        margin: '0px',
    }
}));